import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { API_URL } from '../services/api';
import toast from '../utils/toast';
import CustomPageExpensesTab from './CustomPageExpensesTab';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  EyeSlashIcon,
  ArrowUpTrayIcon,
  BookmarkIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import ExportButtonsWithAll from './ExportButtonsWithAll';
import * as XLSX from 'xlsx';

const CustomPageSubmissionsTable = () => {
  const { pageId } = useParams();
  const { checkPermission, user } = useAuth();
  const navigate = useNavigate();

  const [customPage, setCustomPage] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]); // Array of selected statuses
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibilityPosition, setColumnVisibilityPosition] = useState({ top: null, right: null });
  const columnVisibilityButtonRef = useRef(null);

  // Sorting state - initialize based on whether remaining column exists
  // This will be updated when customPage is loaded
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Header filter state
  const [headerFilters, setHeaderFilters] = useState({});
  const [showHeaderFilters, setShowHeaderFilters] = useState({});
  const [filterInputValues, setFilterInputValues] = useState({});
  const [dateRangeFilters, setDateRangeFilters] = useState({}); // For date range filters: { fieldKey: { from: '', to: '' } }
  const [filterDialog, setFilterDialog] = useState({ open: false, field: null }); // For filter dialog
  const [distinctValues, setDistinctValues] = useState({}); // Store distinct values for each column: { fieldKey: [values] }
  const [loadingDistinctValues, setLoadingDistinctValues] = useState({}); // Track loading state for distinct values
  const [openDropdowns, setOpenDropdowns] = useState({}); // Track which dropdowns are open: { fieldKey: true/false }
  const [dropdownSearchText, setDropdownSearchText] = useState({}); // Search text for each dropdown: { fieldKey: 'search text' }

  // Column visibility state - will be populated dynamically based on table fields
  const [columnVisibility, setColumnVisibility] = useState({});

  // Save/Load View state
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  const [showLoadViewDialog, setShowLoadViewDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  const [currentViewId, setCurrentViewId] = useState(null);
  const [submissionStats, setSubmissionStats] = useState({ statusCounts: {}, deadlineCounts: {} });

  const canView = checkPermission('View Custom Page Submissions');
  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');
  const canImport = checkPermission('Create Custom Page Submission'); // Use same permission as create

  // Import Excel state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({});
  const [dateFormats, setDateFormats] = useState({});
  const [skipLogicColumns, setSkipLogicColumns] = useState([]); // Selected Excel columns to check for duplicates
  const [statusMapping, setStatusMapping] = useState({ type: 'default', column: '', defaultStatus: 'submitted' }); // Status mapping: type can be 'column' or 'default'
  const [deadlineTypeMapping, setDeadlineTypeMapping] = useState({ type: 'default', column: '', defaultType: 'personal' }); // Deadline type mapping: type can be 'column' or 'default'
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    imported: 0,
    skipped: 0,
    errors: [],
    rowResults: [] // Detailed results for each row
  });

  // Initialize sort to remainingDate if it exists (only once when customPage loads)
  const initialSortSetRef = useRef(false);
  useEffect(() => {
    if (customPage && !initialSortSetRef.current) {
      let deadlineFieldName = null;
      if (customPage?.taskSettings?.deadlineField) {
        deadlineFieldName = customPage.taskSettings.deadlineField;
      } else if (customPage?.fields) {
        const deadlineField = customPage.fields.find(f => f.isDeadlineField === true);
        if (deadlineField) {
          deadlineFieldName = deadlineField.name;
        }
      }
      
      // If remaining column exists, set initial sort to remainingDate
      if (deadlineFieldName) {
        setSortField('remainingDate');
        setSortDirection('asc'); // Sort ascending so overdue items appear first
      }
      initialSortSetRef.current = true;
    }
  }, [customPage]); // Only run when customPage changes

  useEffect(() => {
    fetchCustomPage();
    fetchSubmissions();
  }, [pageId, pagination.page, pagination.limit, selectedStatuses, sortField, sortDirection, headerFilters, dateRangeFilters]);

  useEffect(() => {
    if (pageId) {
      fetchSavedViews();
      loadDefaultView();
      fetchSubmissionStats();
    }
  }, [pageId]);

  const fetchSubmissionStats = async () => {
    if (!pageId) return;
    try {
      const response = await api.get(`/custom-pages/${pageId}/submissions/stats`);
      if (response.data?.success && response.data?.data) {
        setSubmissionStats({
          statusCounts: response.data.data.statusCounts || {},
          deadlineCounts: response.data.data.deadlineCounts || {}
        });
      }
    } catch (error) {
      console.error('Error fetching submission stats:', error);
    }
  };

  // Click outside handler is handled by the backdrop div in the JSX


  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
      toast.error('Failed to fetch page details');
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setFilterLoading(true);
      const params = new URLSearchParams();

      // Add status filters (multiple statuses can be selected)
      if (selectedStatuses.length > 0) {
        selectedStatuses.forEach(status => {
          params.append('status', status);
        });
      }

      // Add sorting parameters
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);

      // Add header filters
      Object.keys(headerFilters).forEach(key => {
        const filterValue = headerFilters[key];
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          // Handle array filters (for dropdown multi-select)
          filterValue.forEach(value => {
            params.append(`filters[${key}][]`, value);
          });
        } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
          // Handle string filters (for text fields)
          params.append(`filters[${key}]`, filterValue.trim());
        }
      });

      // Add date range filters
      Object.keys(dateRangeFilters).forEach(key => {
        const range = dateRangeFilters[key];
        if (range && (range.from || range.to)) {
          if (range.from && range.from.trim() !== '') {
            params.append(`filters[${key}][from]`, range.from.trim());
          }
          if (range.to && range.to.trim() !== '') {
            params.append(`filters[${key}][to]`, range.to.trim());
          }
        }
      });

      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await api.get(`/custom-pages/${pageId}/submissions?${params.toString()}`);

      if (response.data.success) {
        setSubmissions(response.data.data.docs);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.totalDocs,
          pages: response.data.data.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  // Save/Load View for custom page submissions layout
  const fetchSavedViews = async () => {
    if (!pageId) return;
    try {
      setLoadingViews(true);
      const response = await api.get(`/user-views/custom-page/${pageId}`);
      if (response.data.success && response.data.data) {
        setSavedViews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching saved views:', error);
    } finally {
      setLoadingViews(false);
    }
  };

  const loadDefaultView = async () => {
    if (!pageId) return;
    try {
      const response = await api.get(`/user-views/custom-page/${pageId}/default`);
      if (response.data.success && response.data.data) {
        applyView(response.data.data, true);
      }
    } catch (error) {
      console.error('Error loading default view:', error);
    }
  };

  const applyView = (view, silent = false) => {
    if (!view || !view.viewSettings) return;
    const { columnVisibility: viewColumnVisibility, sortField: viewSortField, sortDirection: viewSortDirection } = view.viewSettings;
    if (viewColumnVisibility && typeof viewColumnVisibility === 'object') {
      const visibilityObj = viewColumnVisibility instanceof Map
        ? Object.fromEntries(viewColumnVisibility)
        : viewColumnVisibility;
      setColumnVisibility(prev => ({ ...prev, ...visibilityObj }));
    }
    if (viewSortField) setSortField(viewSortField);
    if (viewSortDirection) setSortDirection(viewSortDirection);
    setCurrentViewId(view._id);
    if (!silent) toast.success(`View "${view.name}" loaded`);
  };

  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a view name');
      return;
    }
    if (!pageId) return;
    try {
      setSavingView(true);
      const response = await api.post(`/user-views/custom-page/${pageId}`, {
        name: newViewName.trim(),
        viewSettings: { columnVisibility, sortField, sortDirection },
        isDefault: saveAsDefault
      });
      if (response.data.success) {
        toast.success('View saved successfully');
        setShowSaveViewDialog(false);
        setNewViewName('');
        setSaveAsDefault(false);
        fetchSavedViews();
        if (saveAsDefault) setCurrentViewId(response.data.data._id);
      }
    } catch (error) {
      console.error('Error saving view:', error);
      toast.error(error.response?.data?.message || 'Failed to save view');
    } finally {
      setSavingView(false);
    }
  };

  const handleLoadView = (view) => {
    applyView(view);
    setShowLoadViewDialog(false);
  };

  const handleDeleteView = async (viewId) => {
    if (!window.confirm('Are you sure you want to delete this view?')) return;
    try {
      const response = await api.delete(`/user-views/${viewId}`);
      if (response.data.success) {
        toast.success('View deleted successfully');
        fetchSavedViews();
        if (currentViewId === viewId) setCurrentViewId(null);
      }
    } catch (error) {
      console.error('Error deleting view:', error);
      toast.error('Failed to delete view');
    }
  };

  const handleSetDefaultView = async (viewId) => {
    try {
      const response = await api.put(`/user-views/${viewId}/default`);
      if (response.data.success) {
        toast.success('Default view set successfully');
        fetchSavedViews();
      }
    } catch (error) {
      console.error('Error setting default view:', error);
      toast.error('Failed to set default view');
    }
  };

  // Fetch all submissions for export (without pagination)
  const fetchAllSubmissions = async () => {
    try {
      console.log('📡 Fetching ALL submissions for export...');
      const params = new URLSearchParams();

      // Add status filters (multiple statuses can be selected)
      if (selectedStatuses.length > 0) {
        selectedStatuses.forEach(status => {
          params.append('status', status);
        });
      }

      // Add sorting parameters
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);

      // Add header filters
      Object.keys(headerFilters).forEach(key => {
        const filterValue = headerFilters[key];
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          // Handle array filters (for dropdown multi-select)
          filterValue.forEach(value => {
            params.append(`filters[${key}][]`, value);
          });
        } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
          // Handle string filters (for text fields)
          params.append(`filters[${key}]`, filterValue.trim());
        }
      });

      // Add date range filters
      Object.keys(dateRangeFilters).forEach(key => {
        const range = dateRangeFilters[key];
        if (range.from && range.from.trim() !== '') {
          params.append(`filters[${key}][from]`, range.from.trim());
        }
        if (range.to && range.to.trim() !== '') {
          params.append(`filters[${key}][to]`, range.to.trim());
        }
      });

      params.append('page', '1');
      params.append('limit', pagination.total.toString()); // Get all records

      const response = await api.get(`/custom-pages/${pageId}/submissions?${params.toString()}`);

      if (response.data.success) {
        console.log(`✅ Fetched ${response.data.data.docs.length} submissions for export`);
        return response.data.data.docs;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error fetching all submissions:', error);
      toast.error('Failed to fetch all submissions');
      throw error;
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
      : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
  };

  // Header filter functions
  const handleHeaderFilterChange = (field, value) => {
    setFilterInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHeaderFilterKeyPress = (field, e) => {
    if (e.key === 'Enter') {
      setFilterLoading(true);
      const value = filterInputValues[field] || '';
      setHeaderFilters(prev => ({
        ...prev,
        [field]: value
      }));
      setShowHeaderFilters(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const toggleHeaderFilter = (field) => {
    setShowHeaderFilters(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const clearHeaderFilter = (field) => {
    setFilterLoading(true);
    setHeaderFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setFilterInputValues(prev => {
      const newInputs = { ...prev };
      delete newInputs[field];
      return newInputs;
    });
    setShowHeaderFilters(prev => ({
      ...prev,
      [field]: false
    }));
  };

  const clearAllHeaderFilters = () => {
    setFilterLoading(true);
    setHeaderFilters({});
    setFilterInputValues({});
    setShowHeaderFilters({});
    setDateRangeFilters({});
    setSelectedStatuses([]);
  };

  const toggleStatus = (status) => {
    setFilterLoading(true);
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleDateRangeFilterChange = (fieldKey, type, value) => {
    setDateRangeFilters(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        [type]: value
      }
    }));
  };

  const clearDateRangeFilter = (fieldKey) => {
    setFilterLoading(true);
    setDateRangeFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldKey];
      return newFilters;
    });
  };

  const applyDateRangeFilter = (fieldKey) => {
    setFilterLoading(true);
    // Filter is applied automatically via useEffect
  };

  // Get field options for dropdown fields
  const getFieldOptions = (fieldKey) => {
    // First try to get from tableFields
    const tableField = tableFields.find(f => f.key === fieldKey);
    if (tableField && tableField.fieldOptions) {
      return tableField.fieldOptions;
    }
    // Fallback to customPage fields
    if (!customPage || !customPage.fields) return [];
    const field = customPage.fields.find(f => f.name === fieldKey);
    return field && field.options ? field.options : [];
  };

  // Fetch distinct values for a column
  const fetchDistinctValues = async (fieldKey) => {
    if (!pageId || !fieldKey) return;
    
    // Check if we already have the values
    if (distinctValues[fieldKey]) return;
    
    // Check if already loading
    if (loadingDistinctValues[fieldKey]) return;
    
    try {
      setLoadingDistinctValues(prev => ({ ...prev, [fieldKey]: true }));
      const response = await api.get(`/custom-pages/${pageId}/submissions/distinct-values?fieldName=${fieldKey}`);
      if (response.data.success) {
        setDistinctValues(prev => ({ ...prev, [fieldKey]: response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching distinct values:', error);
      toast.error('Failed to load filter options');
    } finally {
      setLoadingDistinctValues(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  // Sortable and filterable header component
  const SortableHeader = ({ field, onSort }) => {
    const dropdownRef = useRef(null);
    const isDropdownOpen = openDropdowns[field.key] || false;
    const searchText = dropdownSearchText[field.key] || '';
    const selectedValues = Array.isArray(headerFilters[field.key]) ? headerFilters[field.key] : (headerFilters[field.key] ? [headerFilters[field.key]] : []);
    const fieldDistinctValues = distinctValues[field.key] || [];
    const isLoadingValues = loadingDistinctValues[field.key] || false;
    
    // Check if this field should show dropdown filter (showFilter is true or undefined which defaults to true)
    // showFilter === true means explicitly enabled, undefined means default (true), false means disabled
    const shouldShowDropdown = field.filterable && field.showFilter !== false;
    
    const isFilterActive = (field.fieldType === 'date' 
      ? (dateRangeFilters[field.key] && (dateRangeFilters[field.key].from || dateRangeFilters[field.key].to))
      : shouldShowDropdown
      ? (selectedValues.length > 0)
      : field.fieldType === 'dropdown'
      ? (Array.isArray(headerFilters[field.key]) ? headerFilters[field.key].length > 0 : headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '')
      : (headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== ''));

    // Filter distinct values based on search text
    const filteredValues = fieldDistinctValues.filter(value => 
      value.toLowerCase().includes(searchText.toLowerCase())
    );

    // Handle dropdown toggle
    const handleDropdownToggle = () => {
      if (!isDropdownOpen) {
        // Open dropdown and fetch distinct values if not already loaded
        setOpenDropdowns(prev => ({ ...prev, [field.key]: true }));
        fetchDistinctValues(field.key);
      } else {
        setOpenDropdowns(prev => ({ ...prev, [field.key]: false }));
        setDropdownSearchText(prev => ({ ...prev, [field.key]: '' }));
      }
    };

    // Handle value selection
    const handleValueToggle = (value) => {
      const newSelected = selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value];
      
      if (newSelected.length > 0) {
        setHeaderFilters(prev => ({ ...prev, [field.key]: newSelected }));
      } else {
        const newFilters = { ...headerFilters };
        delete newFilters[field.key];
        setHeaderFilters(newFilters);
      }
      setFilterLoading(true);
    };

    // Handle clear filter
    const handleClearFilter = (e) => {
      e.stopPropagation();
      const newFilters = { ...headerFilters };
      delete newFilters[field.key];
      setHeaderFilters(newFilters);
      setFilterLoading(true);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setOpenDropdowns(prev => ({ ...prev, [field.key]: false }));
          setDropdownSearchText(prev => ({ ...prev, [field.key]: '' }));
        }
      };

      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDropdownOpen, field.key]);

    return (
      <div className="flex items-center justify-between relative">
        <button
          onClick={() => field.sortable && onSort(field.key)}
          className={`flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
            field.sortable ? 'cursor-pointer hover:text-gray-700' : 'cursor-default'
          }`}
        >
          <span className="text-xs">{field.label}</span>
          {field.sortable && getSortIcon(field.key)}
        </button>
        
        {field.filterable && (
          <button
            onClick={() => {
              // If showFilter is enabled, fetch distinct values before opening dialog
              if (shouldShowDropdown) {
                fetchDistinctValues(field.key);
              }
              setFilterDialog({ open: true, field });
            }}
            className={`p-1 rounded relative ${
              isFilterActive ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Filter"
          >
            <FunnelIcon className="h-3 w-3" />
            {shouldShowDropdown && selectedValues.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {selectedValues.length}
              </span>
            )}
          </button>
        )}
      </div>
    );
  };

  // Filter Dialog Component
  const FilterDialog = ({ field, onClose }) => {
    const [localFilterValue, setLocalFilterValue] = useState('');
    const [localSelectedOptions, setLocalSelectedOptions] = useState([]); // For multi-select dropdown
    const [localDateRange, setLocalDateRange] = useState({ from: '', to: '' });
    const [searchText, setSearchText] = useState(''); // For searchable distinct values filter
    const shouldShowDistinctValues = field?.showFilter === true; // Check if should use distinct values

    useEffect(() => {
      if (field) {
        // Fetch distinct values if showFilter is enabled
        if (shouldShowDistinctValues) {
          fetchDistinctValues(field.key);
        }
        
        if (field.fieldType === 'date') {
          const range = dateRangeFilters[field.key] || { from: '', to: '' };
          setLocalDateRange(range);
        } else if (field.fieldType === 'dropdown' || shouldShowDistinctValues) {
          // Handle array of selected values for dropdown or distinct values filter
          const filterValue = headerFilters[field.key];
          if (Array.isArray(filterValue)) {
            setLocalSelectedOptions(filterValue);
          } else if (filterValue) {
            // Handle legacy single value (convert to array)
            setLocalSelectedOptions([filterValue]);
          } else {
            setLocalSelectedOptions([]);
          }
        } else {
          setLocalFilterValue(headerFilters[field.key] || '');
        }
      }
    }, [field, shouldShowDistinctValues]);

    const handleDropdownToggle = (option) => {
      setLocalSelectedOptions(prev => {
        if (prev.includes(option)) {
          return prev.filter(v => v !== option);
        } else {
          return [...prev, option];
        }
      });
    };

    const handleApply = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        if (localDateRange.from || localDateRange.to) {
          setDateRangeFilters(prev => ({
            ...prev,
            [field.key]: localDateRange
          }));
        } else {
          clearDateRangeFilter(field.key);
        }
      } else if (field.fieldType === 'dropdown' || shouldShowDistinctValues) {
        // Handle dropdown or distinct values filter (both use array of selected values)
        if (localSelectedOptions.length > 0) {
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localSelectedOptions
          }));
        } else {
          clearHeaderFilter(field.key);
        }
      } else {
        if (localFilterValue.trim()) {
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localFilterValue.trim()
          }));
        } else {
          clearHeaderFilter(field.key);
        }
      }
      onClose();
    };

    const handleClear = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        clearDateRangeFilter(field.key);
      } else {
        clearHeaderFilter(field.key);
      }
      setLocalFilterValue('');
      setLocalSelectedOptions([]);
      setLocalDateRange({ from: '', to: '' });
      onClose();
    };

    if (!field) return null;

    const isFilterActive = field.fieldType === 'date'
      ? (dateRangeFilters[field.key] && (dateRangeFilters[field.key].from || dateRangeFilters[field.key].to))
      : shouldShowDistinctValues || field.fieldType === 'dropdown'
      ? (Array.isArray(headerFilters[field.key]) ? headerFilters[field.key].length > 0 : headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '')
      : (headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filter: {field.label}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {shouldShowDistinctValues && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Values (Multiple)
                </label>
                <div className="mb-2">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search values..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto p-2">
                  {loadingDistinctValues[field.key] ? (
                    <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                  ) : (() => {
                    const fieldDistinctValues = distinctValues[field.key] || [];
                    const filteredValues = fieldDistinctValues.filter(value => 
                      value.toLowerCase().includes(searchText.toLowerCase())
                    );
                    return filteredValues.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {searchText ? 'No matching values' : 'No values available'}
                      </div>
                    ) : (
                      filteredValues.map((value, index) => (
                        <label
                          key={index}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={localSelectedOptions.includes(value)}
                            onChange={() => handleDropdownToggle(value)}
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700 flex-1 truncate">{value}</span>
                        </label>
                      ))
                    );
                  })()}
                </div>
                {localSelectedOptions.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {localSelectedOptions.length} value(s) selected
                  </div>
                )}
              </div>
            )}

            {field.fieldType === 'dropdown' && !shouldShowDistinctValues && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Values (Multiple)
                </label>
                <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto p-2">
                  {getFieldOptions(field.key).map((option, index) => (
                    <label
                      key={index}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={localSelectedOptions.includes(option)}
                        onChange={() => handleDropdownToggle(option)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {localSelectedOptions.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {localSelectedOptions.length} option(s) selected
                  </div>
                )}
              </div>
            )}

            {field.fieldType === 'date' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={localDateRange.from}
                    onChange={(e) => setLocalDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={localDateRange.to}
                    onChange={(e) => setLocalDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {(field.fieldType !== 'dropdown' && field.fieldType !== 'date' && !shouldShowDistinctValues) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter Value
                </label>
                <input
                  type="text"
                  value={localFilterValue}
                  onChange={(e) => setLocalFilterValue(e.target.value)}
                  placeholder={`Enter ${field.label}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            {isFilterActive && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-800"
              >
                Clear Filter
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleViewDetails = async (submission) => {
    try {
      const response = await api.get(`/custom-pages/submissions/${submission._id}`);
      if (response.data.success) {
        const submissionData = response.data.data;
        
        // Convert Map data to regular object if needed
        if (submissionData.data instanceof Map) {
          submissionData.data = Object.fromEntries(submissionData.data);
        }
        
        console.log('Submission data after conversion:', submissionData);
        setSelectedSubmission(submissionData);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    }
  };

  const handleEdit = (submission) => {
    console.log('Edit button clicked!');
    console.log('Submission:', submission);
    console.log('Can update:', canUpdate);
    console.log('Custom page:', customPage);
    
    // Convert Map data to regular object if needed
    let submissionData = submission.data;
    if (submissionData instanceof Map) {
      submissionData = Object.fromEntries(submissionData);
    }
    
    console.log('Submission data after conversion:', submissionData);
    
    // Close detail dialog first
    setShowDetails(false);
    
    // Then set up edit dialog state
    setSelectedSubmission(submission);
    setEditFormData(submissionData || {});
    setShowEdit(true);
    
    console.log('States set - showEdit should be true now');
    console.log('Modal rendering conditions:', {
      showEdit: true,
      selectedSubmission: !!submission,
      customPage: !!customPage
    });
  };

  const handleDelete = (submission) => {
    setSelectedSubmission(submission);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/custom-pages/submissions/${selectedSubmission._id}`);
      toast.success('Submission deleted successfully');
      setShowDelete(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleArchive = async (submissionId) => {
    try {
      // Archive by setting isActive to false
      await api.put(`/custom-pages/submissions/${submissionId}`, {
        isActive: false
      });
      toast.success('Submission archived successfully');
      fetchSubmissions();
    } catch (error) {
      console.error('Error archiving submission:', error);
      toast.error('Failed to archive submission');
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success('Status updated successfully');
        fetchSubmissions();
        fetchSubmissionStats();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleEditSave = async (updatedData) => {
    try {
      console.log('handleEditSave called with updatedData:', updatedData);

      // updatedData now contains { name, data } structure
      const response = await api.put(`/custom-pages/submissions/${selectedSubmission._id}`, updatedData);

      console.log('Update response:', response.data);

      if (response.data.success) {
        toast.success('Submission updated successfully');
        setShowEdit(false);
        setSelectedSubmission(null);
        setEditFormData({});
        fetchSubmissions();
        fetchSubmissionStats();
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error(error.response?.data?.message || 'Failed to update submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatDateWithTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    console.log('renderFieldValue called with:', { fieldType: field.type, fieldName: field.name, value });
    
    // Handle Map data structure
    if (value instanceof Map) {
      console.log('Value is a Map, converting to object');
      value = Object.fromEntries(value);
    }
    
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        console.log('Processing upload field:', field.name, 'Value:', value);
        if (Array.isArray(value) && value.length > 0) {
          console.log('Upload field has files:', value);
          console.log('Number of files:', value.length);
          
          // Simple file display to ensure it works
          return (
            <div className="space-y-2">
              {value.map((file, index) => {
                const fileName = file.fileName || file.name || 'File';
                const fileUrl = file.fileUrl || file.url || file;
                const fileSize = file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size';
                
                console.log(`Rendering file ${index + 1}:`, { fileName, fileUrl, fileSize });
                
                return (
                  <div key={`file-${index}`} className="flex items-center space-x-2 p-2 bg-blue-50 rounded border">
                    <span className="text-blue-600">📄</span>
                    <span className="text-sm font-medium">{fileName}</span>
                    <span className="text-xs text-gray-500">({fileSize})</span>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Download
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }
        console.log('Upload field has no files or is not an array');
        return <span className="text-gray-500">No files uploaded</span>;

      case 'masterdata':
        if (value && typeof value === 'object' && value._id) {
          const masterDataId = value._id;
          const clientName = value.clientName || 'N/A';
          return (
            <a
              href={`/master-data/${masterDataId}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {clientName}
            </a>
          );
        }
        // If value is just an ID string, show it (shouldn't happen after backend population)
        if (value && typeof value === 'string' && value.length === 24) {
          return (
            <a
              href={`/master-data/${value}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {value}
            </a>
          );
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return value.length > 100 ? `${value.substring(0, 100)}...` : value;

      default:
        return value;
    }
  };

  // Calculate remaining time until deadline
  const calculateRemainingTime = (deadlineDate) => {
    if (!deadlineDate) return null;
    
    const deadline = new Date(deadlineDate);
    const now = new Date();
    const diffMs = deadline - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMs < 0) {
      // Overdue
      const overdueDays = Math.abs(diffDays);
      const overdueHours = Math.abs(diffHours);
      if (overdueDays > 0) {
        return `Overdue by ${overdueDays} day${overdueDays > 1 ? 's' : ''}`;
      } else if (overdueHours > 0) {
        return `Overdue by ${overdueHours} hour${overdueHours > 1 ? 's' : ''}`;
      } else {
        return 'Overdue';
      }
    } else {
      // Not yet due
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} left`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
      } else {
        return 'Due now';
      }
    }
  };

  // Get all possible table fields (before filtering by visibility)
  const getAllTableFields = () => {
    const fields = [];

    // Check if deadline field exists (either in taskSettings or as isDeadlineField)
    let deadlineFieldName = null;
    
    // First check if taskSettings.deadlineField is set
    if (customPage?.taskSettings?.deadlineField) {
      deadlineFieldName = customPage.taskSettings.deadlineField;
    } 
    // Otherwise, check for a field with isDeadlineField = true
    else if (customPage?.fields) {
      const deadlineField = customPage.fields.find(f => f.isDeadlineField === true);
      if (deadlineField) {
        deadlineFieldName = deadlineField.name;
      }
    }

    // Add remaining date column as the leftmost column if deadline exists
    if (deadlineFieldName) {
      fields.push({
        key: 'remainingDate',
        label: 'Remaining',
        sortable: true,
        filterable: false,
        deadlineFieldName: deadlineFieldName, // Store the actual deadline field name for sorting
        render: (submission) => {
          // Only use the deadline field value from submission data (column "A")
          if (submission.data) {
            const submissionData = submission.data instanceof Map ? Object.fromEntries(submission.data) : submission.data;
            const deadlineValue = submissionData[deadlineFieldName];
            if (deadlineValue) {
              const remaining = calculateRemainingTime(deadlineValue);
              const isOverdue = remaining && remaining.startsWith('Overdue');
              return (
                <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'}>
                  {remaining}
                </span>
              );
            }
          }
          return '-';
        }
      });
    }

    // Add custom fields that are marked to show in table
    if (customPage?.fields) {
      customPage.fields.forEach(field => {
        // Exclude upload fields from table display
        if (field.showInTable && field.type !== 'upload') {
          // Determine showFilter value (default to true if undefined)
          const fieldShowFilter = field.showFilter !== undefined ? field.showFilter : true;
          
          fields.push({
            key: field.name,
            label: field.label,
            sortable: true,
            // filterable: always true if showFilter is explicitly true, otherwise check showColumnFilters
            filterable: fieldShowFilter === true ? true : (customPage?.showColumnFilters !== false),
            showFilter: fieldShowFilter, // Store showFilter to determine dropdown vs dialog
            fieldType: field.type,
            isDeadlineField: field.isDeadlineField, // Store this for filtering
            render: (submission) => {
              // Check if field should be visible based on deadline type and isDeadlineField
              if (field.isDeadlineField && submission.deadline?.type) {
                let shouldShowField = false;
                
                if (submission.deadline.type === 'personal') {
                  // Personal deadline: Only show to creator
                  const submitterId = submission.submittedBy?._id || submission.submittedBy;
                  shouldShowField = user && (user.id === submitterId || user._id === submitterId);
                } else if (submission.deadline.type === 'group') {
                  // Group deadline: Only show to users in the same group
                  const submitterGroupId = submission.submittedBy?.groupId || submission.submittedBy?.group?._id;
                  const userGroupId = user?.groupId || user?.group?._id;
                  shouldShowField = submitterGroupId && userGroupId && submitterGroupId.toString() === userGroupId.toString();
                }
                
                if (!shouldShowField) {
                  return '-'; // Hide value if user doesn't have access
                }
              }
              
              return renderFieldValue(field, submission.data[field.name]);
            }
          });
        }
      });
    }

    // Add status column
    fields.push({
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      render: (submission) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
          {submission.status}
        </span>
      )
    });

    // Add submitted by column
    fields.push({
      key: 'submittedBy',
      label: 'Submitted By',
      sortable: true,
      filterable: true,
      render: (submission) => (
        <div>
          <div className="font-medium text-gray-900">
            {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
          </div>
          <div className="text-sm text-gray-500">{submission.submittedBy?.email}</div>
        </div>
      )
    });

    // Add submitted date column
    fields.push({
      key: 'createdAt',
      label: 'Submitted Date',
      sortable: true,
      filterable: true,
      render: (submission) => formatDate(submission.createdAt)
    });

    return fields;
  };

  // Initialize column visibility state for all columns when customPage is loaded
  useEffect(() => {
    if (customPage) {
      const allFields = getAllTableFields();
      const initialVisibility = {};
      
      allFields.forEach(field => {
        // Skip actions column (removed)
        if (field.key === 'actions') return;
        
        // Initialize visibility state - default to true for all columns except optional ones
        if (field.key === 'status' || field.key === 'submittedBy' || field.key === 'createdAt') {
          // These were previously optional, keep their current state or default to false
          if (columnVisibility[field.key] === undefined) {
            initialVisibility[field.key] = false;
          } else {
            initialVisibility[field.key] = columnVisibility[field.key];
          }
        } else {
          // All other columns default to visible
          if (columnVisibility[field.key] === undefined) {
            initialVisibility[field.key] = true;
          } else {
            initialVisibility[field.key] = columnVisibility[field.key];
          }
        }
      });
      
      // Only update if there are new fields or missing fields
      const hasNewFields = Object.keys(initialVisibility).some(key => !(key in columnVisibility));
      const hasMissingFields = Object.keys(columnVisibility).some(key => 
        key !== 'actions' && !allFields.find(f => f.key === key)
      );
      
      if (hasNewFields || hasMissingFields) {
        setColumnVisibility(prev => ({ ...prev, ...initialVisibility }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPage]);

  // Get fields that should be shown in table (filtered by visibility)
  const getTableFields = () => {
    const allFields = getAllTableFields();
    
    // Filter fields based on column visibility (default to true if not set)
    const visibleFields = allFields.filter(field => {
      // Skip actions column
      if (field.key === 'actions') return false;
      // Check visibility state, default to true if not set
      return columnVisibility[field.key] !== false;
    });

    return visibleFields;
  };

  // Get all fields for export (including all form fields, not just showInTable ones)
  const getExportFields = () => {
    const fields = [];

    // Add ALL custom fields for export (including upload fields)
    if (customPage?.fields) {
      customPage.fields.forEach(field => {
        fields.push({
          key: `data.${field.name}`, // Use nested key for form data
          header: field.label,
          width: 20
        });
      });
    }

    // Always add status, submitted by, and submitted date for export
    fields.push(
      { header: 'Status', key: 'status', width: 12 },
      { header: 'Submitted By', key: 'submittedBy.firstName', width: 20 },
      { header: 'Submitted Date', key: 'createdAt', width: 15 }
    );

    return fields;
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view submissions.</p>
      </div>
    );
  }

  const tableFields = getTableFields();

  // Submission Details Modal Component
  const SubmissionDetailsModal = ({ submission: initialSubmission, customPage, onClose, onStatusChange, onRefreshDashboard, canUpdate, canDelete, onEdit, onArchive }) => {
    const [submission, setSubmission] = useState(initialSubmission);
    const [submissionComments, setSubmissionComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [submissionHistory, setSubmissionHistory] = useState([]);
    const [editingField, setEditingField] = useState(null); // Track which field is being edited: { fieldName: string, type: string }
    const [editingStatus, setEditingStatus] = useState(false); // Track if status is being edited
    const [editValue, setEditValue] = useState(''); // Current edit value
    const [savingField, setSavingField] = useState(false); // Track if saving a field
    const [masterDataList, setMasterDataList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [masterDataSearchTerm, setMasterDataSearchTerm] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    // Document Inventory State
    const [documentInventory, setDocumentInventory] = useState([]);
    const [documentDate, setDocumentDate] = useState('');
    const [documentFile, setDocumentFile] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const { user } = useAuth();

    // Update submission when prop changes
    useEffect(() => {
      setSubmission(initialSubmission);
    }, [initialSubmission]);

    // Fetch updated submission data
    const refreshSubmission = async () => {
      if (!submission?._id) return;
      try {
        const response = await api.get(`/custom-pages/submissions/${submission._id}`);
        if (response.data.success) {
          const updatedSubmission = response.data.data;
          setSubmission(updatedSubmission);
          // Update comments and history
          if (updatedSubmission.submissionComments && Array.isArray(updatedSubmission.submissionComments)) {
            setSubmissionComments(updatedSubmission.submissionComments);
          }
          if (updatedSubmission.history && Array.isArray(updatedSubmission.history)) {
            setSubmissionHistory(updatedSubmission.history);
          }
        }
      } catch (error) {
        console.error('Error refreshing submission:', error);
      }
    };

    useEffect(() => {
      // Initialize submission comments (only additional comments, not the initial comment)
      if (submission) {
        // Only add submissionComments array, not the initial comment
        // The initial comment is displayed separately
        if (submission.submissionComments && Array.isArray(submission.submissionComments)) {
          setSubmissionComments(submission.submissionComments);
        } else {
          setSubmissionComments([]);
        }
        
        // Initialize history
        if (submission.history && Array.isArray(submission.history)) {
          setSubmissionHistory(submission.history);
        } else {
          setSubmissionHistory([]);
        }
      } else {
        setSubmissionComments([]);
        setSubmissionHistory([]);
      }
    }, [submission]);

    // Fetch master data and user lists when needed
    useEffect(() => {
      const fetchMasterData = async () => {
        try {
          const response = await api.get('/master-data');
          if (response.data.success) {
            setMasterDataList(response.data.data || []);
          } else {
            setMasterDataList(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching master data:', error);
          setMasterDataList([]);
        }
      };

      const fetchUsers = async () => {
        try {
          const response = await api.get('/users');
          if (response.data.success) {
            setUserList(response.data.data || []);
          } else {
            setUserList(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          setUserList([]);
        }
      };

      // Check if custom page has masterdata or user fields
      const hasMasterDataField = customPage?.fields?.some(f => f.type === 'masterdata');
      const hasUserField = customPage?.fields?.some(f => f.type === 'user');

      if (hasMasterDataField) {
        fetchMasterData();
      }
      if (hasUserField) {
        fetchUsers();
      }
    }, [customPage]);

    // Fetch document inventory if enabled
    useEffect(() => {
      if (customPage?.allowDocumentInventory && submission?._id) {
        fetchDocumentInventory();
      }
    }, [customPage, submission?._id]);

    const fetchDocumentInventory = async () => {
      if (!submission?._id) return;
      try {
        const response = await api.get(`/custom-pages/submissions/${submission._id}/document-inventory`);
        if (response.data.success) {
          setDocumentInventory(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching document inventory:', error);
      }
    };

    const handleDocumentUpload = async (e) => {
      e.preventDefault();
      if (!documentFile || !documentDate) {
        toast.error('Please select a file and document date');
        return;
      }

      setUploadingDocument(true);
      try {
        const formData = new FormData();
        formData.append('file', documentFile);
        formData.append('documentDate', documentDate);

        const response = await api.post(
          `/custom-pages/submissions/${submission._id}/document-inventory`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );

        if (response.data.success) {
          toast.success('Document uploaded successfully');
          setDocumentDate('');
          setDocumentFile(null);
          // Reset file input
          const fileInput = document.querySelector('input[type="file"]');
          if (fileInput) fileInput.value = '';
          await fetchDocumentInventory();
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error(error.response?.data?.message || 'Failed to upload document');
      } finally {
        setUploadingDocument(false);
      }
    };

    const handleDeleteDocument = async (documentId) => {
      if (!window.confirm('Are you sure you want to delete this document?')) return;

      try {
        const response = await api.delete(
          `/custom-pages/submissions/${submission._id}/document-inventory/${documentId}`
        );

        if (response.data.success) {
          toast.success('Document deleted successfully');
          await fetchDocumentInventory();
        }
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Failed to delete document');
      }
    };

    const getFullFileUrl = (relativePath) => {
      if (!relativePath) return '';
      // If it's already a full URL, return as is
      if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
        return relativePath;
      }
      // Remove leading slash if present
      const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
      // Get base URL without /api
      const baseUrl = API_URL.replace('/api', '');
      return `${baseUrl}/${cleanPath}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const formatHistoryDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const getHistoryTypeLabel = (type) => {
      const labels = {
        'created': 'Created',
        'field_change': 'Field Changed',
        'status_change': 'Status Changed',
        'comment_added': 'Comment Added',
        'document_verification_added': 'Document Added',
        'document_verification_updated': 'Document Updated',
        'document_verification_removed': 'Document Removed'
      };
      return labels[type] || type;
    };

    const getHistoryTypeIcon = (type) => {
      switch (type) {
        case 'created':
          return '✨';
        case 'field_change':
          return '✏️';
        case 'status_change':
          return '🔄';
        case 'comment_added':
          return '💬';
        case 'document_verification_added':
          return '📄';
        case 'document_verification_updated':
          return '📝';
        case 'document_verification_removed':
          return '🗑️';
        default:
          return '📌';
      }
    };

    const renderHistoryEntry = (entry) => {
      const changedBy = entry.changedBy ? 
        `${entry.changedBy.firstName || ''} ${entry.changedBy.lastName || ''}`.trim() || entry.changedBy.email || 'Unknown' :
        'Unknown';

      switch (entry.type) {
        case 'created':
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{getHistoryTypeLabel(entry.type)}</div>
                <div className="text-sm text-gray-600">by {changedBy}</div>
                <div className="text-xs text-gray-500 mt-1">{formatHistoryDate(entry.changedAt)}</div>
              </div>
            </div>
          );

        case 'field_change':
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {getHistoryTypeLabel(entry.type)}: <span className="text-blue-600">{entry.fieldLabel || entry.fieldName}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  <span className="line-through text-red-600">{entry.oldValue || '(empty)'}</span>
                  {' → '}
                  <span className="text-green-600 font-medium">{entry.newValue || '(empty)'}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  by {changedBy} • {formatHistoryDate(entry.changedAt)}
                </div>
              </div>
            </div>
          );

        case 'status_change':
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{getHistoryTypeLabel(entry.type)}</div>
                <div className="text-sm text-gray-600 mt-1">
                  <span className="line-through text-red-600">{entry.oldValue || '(empty)'}</span>
                  {' → '}
                  <span className="text-green-600 font-medium">{entry.newValue || '(empty)'}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  by {changedBy} • {formatHistoryDate(entry.changedAt)}
                </div>
              </div>
            </div>
          );

        case 'comment_added':
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{getHistoryTypeLabel(entry.type)}</div>
                <div className="text-sm text-gray-700 mt-1 bg-gray-50 p-2 rounded border-l-2 border-blue-400">
                  {entry.comment}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  by {changedBy} • {formatHistoryDate(entry.changedAt)}
                </div>
              </div>
            </div>
          );

        case 'document_verification_added':
        case 'document_verification_updated':
        case 'document_verification_removed':
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {getHistoryTypeLabel(entry.type)}: <span className="text-blue-600">{entry.documentName}</span>
                </div>
                {entry.oldValue && entry.newValue && (
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="line-through text-red-600">{entry.oldValue}</span>
                    {' → '}
                    <span className="text-green-600 font-medium">{entry.newValue}</span>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  by {changedBy} • {formatHistoryDate(entry.changedAt)}
                </div>
              </div>
            </div>
          );

        default:
          return (
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{getHistoryTypeIcon(entry.type)}</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{getHistoryTypeLabel(entry.type)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  by {changedBy} • {formatHistoryDate(entry.changedAt)}
                </div>
              </div>
            </div>
          );
      }
    };

    const handleAddComment = async () => {
      if (!newComment || !newComment.trim()) return;

      setSubmittingComment(true);
      try {
        const response = await api.post(`/custom-pages/submissions/${submission._id}/comments`, {
          text: newComment.trim()
        });
        
        if (response.data.success) {
          // Refresh submission to get updated history
          await refreshSubmission();
          setNewComment('');
          toast.success('Comment added successfully');
        }
      } catch (error) {
        console.error('Error adding comment:', error);
        toast.error(error.response?.data?.message || 'Failed to add comment');
      } finally {
        setSubmittingComment(false);
      }
    };

    const renderFieldValue = (field, value) => {
      if (!value) return '-';

      switch (field.type) {
        case 'upload':
          if (Array.isArray(value) && value.length > 0) {
            return (
              <div className="space-y-1">
                {value.map((file, index) => {
                  // Handle different file object structures
                  const fileUrl = file.fileUrl || file.url || '';
                  const fileName = file.fileName || file.name || file.originalName || 'File';
                  
                  if (!fileUrl) {
                    return (
                      <div key={index} className="text-sm text-gray-500">
                        {fileName} (No URL available)
                      </div>
                    );
                  }
                  
                  return (
                    <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                        {fileName}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          }
          return 'No files uploaded';

        case 'masterdata':
          if (value && typeof value === 'object' && value._id) {
            const masterDataId = value._id;
            const clientName = value.clientName || 'N/A';
            return (
              <a
                href={`/master-data/${masterDataId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {clientName}
              </a>
            );
          }
          // If value is just an ID string, show it (shouldn't happen after backend population)
          if (value && typeof value === 'string' && value.length === 24) {
            return (
              <a
                href={`/master-data/${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {value}
              </a>
            );
          }
          return value || '-';

        case 'user':
          if (value && typeof value === 'object') {
            return `${value.firstName} ${value.lastName}`;
          }
          return value || '-';

        case 'date':
          return formatDate(value);

        case 'multitext':
          return value;

        default:
          return value;
      }
    };

    // Render inline edit field based on field type
    const renderInlineEditField = (field, value, setValue) => {
      switch (field.type) {
        case 'masterdata':
          const filteredMasterData = masterDataList.filter(item =>
            item.clientName?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
            item.clientEmail?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
            item.clientAddress?.toLowerCase().includes(masterDataSearchTerm.toLowerCase())
          );

          return (
            <div className="relative masterdata-dropdown">
              <input
                type="text"
                value={masterDataSearchTerm}
                onChange={(e) => {
                  setMasterDataSearchTerm(e.target.value);
                  setShowMasterDataDropdown(true);
                }}
                onFocus={() => {
                  setShowMasterDataDropdown(true);
                }}
                placeholder={value && typeof value === 'object' && value.clientName ? value.clientName : "Search company..."}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {showMasterDataDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {masterDataList.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">Loading master data...</div>
                  ) : filteredMasterData.length > 0 ? (
                    filteredMasterData.map((item) => (
                      <div
                        key={item._id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setValue(item);
                          setMasterDataSearchTerm(item.clientName);
                          setShowMasterDataDropdown(false);
                        }}
                      >
                        <div className="font-medium text-sm text-gray-900">{item.clientName}</div>
                        {item.clientEmail && (
                          <div className="text-xs text-gray-500">{item.clientEmail}</div>
                        )}
                        {item.clientAddress && (
                          <div className="text-xs text-gray-500">{item.clientAddress}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No master data found</div>
                  )}
                </div>
              )}
              {value && typeof value === 'object' && value.clientName && (
                <button
                  type="button"
                  onClick={() => {
                    setValue(null);
                    setMasterDataSearchTerm('');
                  }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
              {/* Click outside to close dropdown */}
              {showMasterDataDropdown && (
                <div
                  className="fixed inset-0 z-0"
                  onClick={() => {
                    // Restore search term to current value if clicking outside
                    if (value && typeof value === 'object' && value.clientName) {
                      setMasterDataSearchTerm(value.clientName);
                    }
                    setShowMasterDataDropdown(false);
                  }}
                />
              )}
            </div>
          );

        case 'user':
          // Get current selected value display
          const currentUser = value && typeof value === 'object' 
            ? `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email || 'Select User'
            : 'Select User';
          const filteredUsers = userList.filter(user =>
            `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
          );

          return (
            <div className="relative">
              <input
                type="text"
                value={userSearchTerm || (value && typeof value === 'object' 
                  ? `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email || ''
                  : '')}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => {
                  setShowUserDropdown(true);
                  // If there's a current value, show it in search term
                  if (value && typeof value === 'object' && !userSearchTerm) {
                    const displayName = `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email || '';
                    if (displayName) {
                      setUserSearchTerm(displayName);
                    }
                  }
                }}
                placeholder="Search user..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {showUserDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setShowUserDropdown(false);
                      setUserSearchTerm('');
                    }}
                  />
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((userItem, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setValue(userItem);
                            setShowUserDropdown(false);
                            setUserSearchTerm('');
                          }}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-sm text-gray-900">
                            {userItem.firstName} {userItem.lastName}
                          </div>
                          {userItem.email && (
                            <div className="text-xs text-gray-500">{userItem.email}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );

        case 'dropdown':
          return (
            <select
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              <option value="">Select an option</option>
              {(field.options || []).map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );

        case 'date':
          // Convert date to YYYY-MM-DD format for input
          const dateValue = value ? (typeof value === 'string' ? value.split('T')[0] : new Date(value).toISOString().split('T')[0]) : '';
          return (
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          );

        case 'number':
          return (
            <input
              type="number"
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          );

        case 'multitext':
          return (
            <textarea
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          );

        case 'text':
        default:
          return (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          );
      }
    };

    // Handle saving a field value
    const handleSaveField = async (fieldName, newValue, fieldType) => {
      setSavingField(true);
      try {
        // Prepare the update data
        let submissionData = submission.data;
        if (submissionData instanceof Map) {
          submissionData = Object.fromEntries(submissionData);
        }
        
        // Update the field value
        const updatedData = {
          ...submissionData,
          [fieldName]: newValue
        };

        // Send update request
        const response = await api.put(`/custom-pages/submissions/${submission._id}`, {
          data: updatedData
        });

        if (response.data.success) {
          // Refresh submission to get updated data and history
          await refreshSubmission();
          setEditingField(null);
          setEditValue('');
          toast.success(`${customPage?.fields?.find(f => f.name === fieldName)?.label || fieldName} updated successfully`);
        } else {
          toast.error('Failed to update field');
        }
      } catch (error) {
        console.error('Error updating field:', error);
        toast.error(error.response?.data?.message || 'Failed to update field');
      } finally {
        setSavingField(false);
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'draft':
          return 'bg-gray-100 text-gray-800';
        case 'submitted':
          return 'bg-blue-100 text-blue-800';
        case 'approved':
          return 'bg-green-100 text-green-800';
        case 'rejected':
          return 'bg-red-100 text-red-800';
        case 'completed':
          return 'bg-purple-100 text-purple-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Submission Details</h2>
              <p className="text-sm text-gray-600 mt-1">ID: {submission._id.slice(-8)}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                History
                {submissionHistory.length > 0 && (
                  <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                    {submissionHistory.length}
                  </span>
                )}
              </button>
              {customPage && customPage.allowDocumentInventory === true && (
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'upload'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Upload
                  {documentInventory.length > 0 && (
                    <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                      {documentInventory.length}
                    </span>
                  )}
                </button>
              )}
              {customPage && customPage.allowExpensesTab === true && (
                <button
                  onClick={() => setActiveTab('expenses')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'expenses'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Expenses
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Submission ID</label>
                <p className="text-sm text-gray-900 font-medium">
                  {submission._id.slice(-8)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                {!editingStatus ? (
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                    {canUpdate && (
                      <button
                        onClick={() => {
                          setEditingStatus(true);
                          setEditValue(submission.status);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit Status"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <select
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    >
                      {(() => {
                        let statusList = (customPage?.customStatuses && Array.isArray(customPage.customStatuses) && customPage.customStatuses.length > 0)
                          ? [...customPage.customStatuses]
                          : ['draft', 'submitted', 'approved', 'rejected', 'completed'];
                        
                        // Ensure 'submitted' status is always included
                        if (!statusList.includes('submitted')) {
                          statusList = [...statusList, 'submitted'];
                        }
                        
                        return statusList.map((status) => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ));
                      })()}
                    </select>
                    <button
                      onClick={async () => {
                        if (editValue !== submission.status) {
                          setSavingField(true);
                          try {
                            // Use onStatusChange which handles the API call and parent state update
                            await onStatusChange(submission._id, editValue);
                            if (onRefreshDashboard) onRefreshDashboard();
                            // Update local submission state immediately for instant UI feedback
                            setSubmission(prev => ({
                              ...prev,
                              status: editValue
                            }));
                            // Refresh to get updated history
                            await refreshSubmission();
                            setEditingStatus(false);
                            // Note: toast is already shown by handleStatusChange
                          } catch (error) {
                            console.error('Error updating status:', error);
                            toast.error(error.response?.data?.message || 'Failed to update status');
                            setSavingField(false);
                          }
                        } else {
                          setEditingStatus(false);
                        }
                      }}
                      disabled={savingField}
                      className="px-2 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {savingField ? 'Saving...' : '✓'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingStatus(false);
                        setEditValue('');
                      }}
                      disabled={savingField}
                      className="px-2 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Submitted By</label>
                <p className="text-sm text-gray-900">
                  {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                </p>
                <p className="text-sm text-gray-500">{submission.submittedBy?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
                <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
              </div>
            </div>

            {/* Form Data */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Form Data</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                {customPage?.fields?.map((field) => {
                  // Check if field should be visible based on deadline type and isDeadlineField
                  let shouldShowField = true;
                  
                  if (field.isDeadlineField && submission.deadline?.type) {
                    if (submission.deadline.type === 'personal') {
                      // Personal deadline: Only show to creator
                      const submitterId = submission.submittedBy?._id || submission.submittedBy;
                      shouldShowField = user && (user.id === submitterId || user._id === submitterId);
                    } else if (submission.deadline.type === 'group') {
                      // Group deadline: Only show to users in the same group
                      const submitterGroupId = submission.submittedBy?.groupId || submission.submittedBy?.group?._id;
                      const userGroupId = user?.groupId || user?.group?._id;
                      shouldShowField = submitterGroupId && userGroupId && submitterGroupId.toString() === userGroupId.toString();
                    }
                  }
                  
                  // Skip rendering if field should not be shown
                  if (!shouldShowField) {
                    return null;
                  }
                  
                  // Ensure submission.data is properly converted from Map if needed
                  let submissionData = submission.data;
                  if (submissionData instanceof Map) {
                    submissionData = Object.fromEntries(submissionData);
                  }
                  
                  const value = submissionData ? submissionData[field.name] : null;
                  
                  const isEditing = editingField?.fieldName === field.name;
                  
                  return (
                    <div key={field.name} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.isDeadlineField && (
                            <span className="ml-2 text-xs text-blue-600 font-normal">(Deadline Field)</span>
                          )}
                        </label>
                        {canUpdate && !isEditing && field.type !== 'upload' && (
                          <button
                            onClick={() => {
                              setEditingField({ fieldName: field.name, type: field.type });
                              // For masterdata and user fields, preserve the object, otherwise use the value directly
                              if (field.type === 'masterdata' || field.type === 'user') {
                                setEditValue(value || null);
                                // Initialize search term with current value if it exists
                                if (field.type === 'masterdata' && value && typeof value === 'object') {
                                  setMasterDataSearchTerm(value.clientName || '');
                                } else if (field.type === 'masterdata') {
                                  setMasterDataSearchTerm('');
                                } else if (field.type === 'user' && value && typeof value === 'object') {
                                  setUserSearchTerm(`${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email || '');
                                } else if (field.type === 'user') {
                                  setUserSearchTerm('');
                                }
                              } else {
                                setEditValue(value || '');
                              }
                              // Reset dropdown states
                              setShowMasterDataDropdown(false);
                              setShowUserDropdown(false);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title={`Edit ${field.label}`}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {!isEditing ? (
                        <div className="text-sm text-gray-900 mb-2">
                          {renderFieldValue(field, value)}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {renderInlineEditField(field, editValue, setEditValue)}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={async () => {
                                await handleSaveField(field.name, editValue, field.type);
                              }}
                              disabled={savingField}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {savingField ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingField(null);
                                setEditValue('');
                              }}
                              disabled={savingField}
                              className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Deadline Section */}
            {submission.deadline && submission.deadline.type && (() => {
              // Check deadline visibility
              let showDeadline = false;
              if (submission.deadline.type === 'group') {
                // Show to all users in the same group as the submitter
                const submitterGroupId = submission.submittedBy?.groupId || submission.submittedBy?.group?._id;
                const userGroupId = user?.groupId || user?.group?._id;
                showDeadline = submitterGroupId && userGroupId && submitterGroupId.toString() === userGroupId.toString();
              } else if (submission.deadline.type === 'personal') {
                // Only show to creator
                const submitterId = submission.submittedBy?._id || submission.submittedBy;
                showDeadline = user && (user.id === submitterId || user._id === submitterId);
              }
              
              return showDeadline ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Deadline</h3>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-sm">
                      {submission.deadline.date && (
                        <>
                          <div className="font-semibold text-blue-900 mb-1">Date:</div>
                          <div className="text-blue-700 mb-2">
                            {new Date(submission.deadline.date).toLocaleDateString()}
                          </div>
                        </>
                      )}
                      <div className="font-semibold text-blue-900 mb-1">Type:</div>
                      <div className="text-blue-700">
                        {submission.deadline.type === 'personal' ? 'Personal' : 'Group'}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Comments Section - Always available */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Comments</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                {/* Initial Comment */}
                {submission.comment && (
                  <div className="mb-3 text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-200">
                    <div className="font-medium">Initial Comment:</div>
                    <div className="mt-1 whitespace-pre-wrap">{submission.comment}</div>
                  </div>
                )}

                {/* Existing Comments */}
                {submissionComments.length > 0 && (
                  <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                    {submissionComments.map((comment, index) => (
                      <div key={index} className="bg-white p-3 rounded border-l-2 border-blue-400">
                        <div className="text-sm text-gray-900 whitespace-pre-wrap">{comment.text}</div>
                        <div className="text-xs text-gray-500 mt-2">
                          {comment.createdBy?.firstName} {comment.createdBy?.lastName} • {formatDate(comment.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Comment */}
                <div className="space-y-2">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={submittingComment || !newComment.trim()}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </div>
            </div>

            {/* Document Verifications */}
            {submission.documentVerifications && submission.documentVerifications.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Document Verifications</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {submission.documentVerifications.map((docVer, index) => (
                    <div key={index} className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {docVer.documentName}
                      </label>
                      <div className="text-sm text-gray-900">
                        {docVer.files && docVer.files.length > 0 ? (
                          <div className="space-y-1">
                            {docVer.files.map((file, fileIndex) => (
                              <div key={fileIndex} className="text-blue-600 hover:text-blue-800">
                                <a href={file.fileUrl} target="_blank" rel="noopener noreferrer">
                                  {file.fileName}
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          'No files uploaded'
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status Change */}
            {canUpdate && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Change Status</h3>
                <div className="flex space-x-2 flex-wrap gap-2">
                  {(() => {
                    // Use custom statuses if they exist and have items, otherwise use default
                    let statusList = (customPage?.customStatuses && Array.isArray(customPage.customStatuses) && customPage.customStatuses.length > 0)
                      ? [...customPage.customStatuses]
                      : ['draft', 'submitted', 'approved', 'rejected', 'completed'];
                    
                    // Ensure 'submitted' status is always included
                    if (!statusList.includes('submitted')) {
                      statusList = [...statusList, 'submitted'];
                    }
                    
                    return statusList.map((status) => (
                      <button
                        key={status}
                        onClick={async () => {
                          await onStatusChange(submission._id, status);
                          if (onRefreshDashboard) onRefreshDashboard();
                          // Refresh submission to get updated history
                          await refreshSubmission();
                        }}
                        className={`px-3 py-1 text-sm rounded-full ${
                          submission.status === status
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

          </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Change History</h3>
                {submissionHistory.length === 0 ? (
                  <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
                    No history available for this submission.
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4 max-h-96 overflow-y-auto">
                    {submissionHistory.map((entry, index) => (
                      <div
                        key={index}
                        className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
                      >
                        {renderHistoryEntry(entry)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'upload' && customPage && customPage.allowDocumentInventory === true && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Document Inventory</h3>
                
                {/* Documents Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Document Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          File Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Uploaded Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Uploaded By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {documentInventory.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                            No documents uploaded yet
                          </td>
                        </tr>
                      ) : (
                        documentInventory.map((doc, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(doc.documentDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {doc.originalName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(doc.uploadedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {doc.uploadedBy?.firstName} {doc.uploadedBy?.lastName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <a
                                href={getFullFileUrl(doc.fileUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 mr-3"
                              >
                                View
                              </a>
                              {canUpdate && (
                                <button
                                  onClick={() => handleDeleteDocument(doc._id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Upload Form */}
                {canUpdate && (
                  <div className="bg-gray-50 p-4 rounded-lg mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Upload New Document</h4>
                    <form onSubmit={handleDocumentUpload} className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Date *
                          </label>
                          <input
                            type="date"
                            value={documentDate}
                            onChange={(e) => setDocumentDate(e.target.value)}
                            required
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            File *
                          </label>
                          <input
                            type="file"
                            onChange={(e) => setDocumentFile(e.target.files[0])}
                            required
                            accept={customPage.documentInventorySettings?.allowedFileTypes?.join(',') || '*'}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Max size: {customPage.documentInventorySettings?.maxFileSize || 10}MB
                        {customPage.documentInventorySettings?.allowedFileTypes && 
                          ` | Allowed types: ${customPage.documentInventorySettings.allowedFileTypes.join(', ')}`
                        }
                      </p>
                      <button
                        type="submit"
                        disabled={uploadingDocument}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'expenses' && customPage && customPage.allowExpensesTab === true && (
            <CustomPageExpensesTab
              submissionId={submission._id}
              customPage={customPage}
              canUpdate={canUpdate}
              formatDate={formatDate}
            />
          )}

          {/* Action Buttons - Always visible */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-4">
            {canUpdate && onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent any event bubbling
                  onEdit(submission);
                  // Don't call onClose here - handleEdit will handle closing the detail dialog
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Edit</span>
              </button>
            )}
            {canDelete && onArchive && (
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to archive this submission? It will be hidden from the list.')) {
                    await onArchive(submission._id);
                    onClose();
                  }
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Archive</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Edit Submission Modal Component
  const EditSubmissionModal = ({ submission, customPage, onClose, onSave }) => {
    console.log('EditSubmissionModal rendering with props:', {
      submission: !!submission,
      customPage: !!customPage,
      onClose: !!onClose,
      onSave: !!onSave
    });
    
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [masterDataSearchTerm, setMasterDataSearchTerm] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);
    const [documentVerifications, setDocumentVerifications] = useState([]);
    const [documentUploads, setDocumentUploads] = useState({});
    const [userList, setUserList] = useState([]);
    const [masterDataList, setMasterDataList] = useState([]);

    useEffect(() => {
      // Initialize form data with current submission data
      if (submission && submission.data) {
        // Ensure submission.data is properly converted from Map if needed
        let submissionData = submission.data;
        if (submissionData instanceof Map) {
          submissionData = Object.fromEntries(submissionData);
        }
        
        setFormData(submissionData);

        // Initialize search terms for user and masterdata fields
        const userField = customPage?.fields.find(f => f.type === 'user');
        const masterDataField = customPage?.fields.find(f => f.type === 'masterdata');

        if (userField && submissionData[userField.name]) {
          const user = submissionData[userField.name];
          setUserSearchTerm(`${user.firstName} ${user.lastName}`);
        }

        if (masterDataField && submissionData[masterDataField.name]) {
          const masterDataItem = submissionData[masterDataField.name];
          setMasterDataSearchTerm(masterDataItem.clientName || '');
        }
      }

      // Fetch users and master data
      fetchUsers();
      fetchMasterData();
      fetchDocumentVerifications();
    }, [submission, customPage]);

    // Click outside handler to close dropdowns
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.user-dropdown')) {
          setShowUserDropdown(false);
        }
        if (!event.target.closest('.masterdata-dropdown')) {
          setShowMasterDataDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const fetchUsers = async () => {
      try {
        const response = await api.get('/users');
        if (response.data.success) {
          setUserList(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    const fetchMasterData = async () => {
      try {
        const response = await api.get('/master-data');
        setMasterDataList(response.data.data);
      } catch (error) {
        console.error('Error fetching master data:', error);
      }
    };

    const fetchDocumentVerifications = async () => {
      try {
        const response = await api.get('/custom-documents');
        if (response.data.success) {
          const docs = response.data.data.docs || response.data.data || [];
          setDocumentVerifications(docs);

          // Initialize document uploads from existing Checklist
          const initialUploads = {};
          if (submission?.documentVerifications) {
            submission.documentVerifications.forEach(docVer => {
              // If there are files in the Checklist, use the first one
              // Otherwise, create a file object from the Checklist data
              if (docVer.files && docVer.files.length > 0) {
                initialUploads[docVer.documentId] = docVer.files[0];
              } else if (docVer.fileUrl) {
                // Create a file object from the Checklist data
                initialUploads[docVer.documentId] = {
                  fileName: docVer.fileName || docVer.documentName,
                  fileUrl: docVer.fileUrl,
                  originalName: docVer.fileName || docVer.documentName,
                  mimeType: 'application/octet-stream', // Default mime type
                  size: 0 // Unknown size
                };
              }
            });
          }
          setDocumentUploads(initialUploads);
        }
      } catch (error) {
        console.error('Error fetching Checklist:', error);
      }
    };

    const handleInputChange = (fieldName, value) => {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));

      // Clear error for this field
      if (errors[fieldName]) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: null
        }));
      }
    };

    const handleDocumentUpload = (documentId, files) => {
      setDocumentUploads(prev => ({
        ...prev,
        [documentId]: files
      }));
    };

    const uploadFileToServer = async (file) => {
      try {
        console.log('uploadFileToServer called with file:', file);

        const formData = new FormData();
        formData.append('file', file);

        console.log('Sending upload request to /upload');
        const response = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        console.log('Upload response:', response.data);

        if (response.data.success) {
          const uploadedFile = {
            fileName: file.name,
            fileUrl: response.data.url,
            originalName: file.name,
            mimeType: file.type,
            size: file.size
          };
          console.log('Returning uploaded file object:', uploadedFile);
          return uploadedFile;
        } else {
          console.error('Upload failed - response not successful:', response.data);
          throw new Error('Upload failed');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error('Failed to upload file');
        return null;
      }
    };

    const handleFileUpload = async (documentId, files) => {
      try {
        console.log('handleFileUpload called with:', { documentId, files });
        
        // If no files provided, clear the upload
        if (!files || files.length === 0) {
          console.log('No files provided, clearing document uploads for', documentId);
          handleDocumentUpload(documentId, null);
          return;
        }
        
        // Take the first file only (since we're using single file structure)
        const file = files[0];
        console.log('Processing file:', file);
        
        // If the file is already a File object (new upload), upload it
        if (file instanceof File) {
          console.log('Uploading new file to server:', file.name);
          const uploadedFile = await uploadFileToServer(file);
          if (uploadedFile) {
            console.log('File uploaded successfully:', uploadedFile);
            handleDocumentUpload(documentId, uploadedFile);
          }
        } else {
          // If it's already an uploaded file object, keep it as is
          console.log('File already uploaded, keeping as is:', file);
          handleDocumentUpload(documentId, file);
        }
      } catch (error) {
        console.error('Error handling file upload:', error);
        toast.error('Failed to upload files');
      }
    };

    const getRequiredDocumentVerifications = () => {
      if (!customPage || !customPage.documentVerifications || !documentVerifications.length) {
        return [];
      }

      return documentVerifications.filter(doc =>
        customPage.documentVerifications.some(requiredDoc =>
          requiredDoc === doc._id || requiredDoc._id === doc._id
        )
      );
    };

    const validateForm = () => {
      const newErrors = {};

      customPage.fields.forEach(field => {
        if (field.required) {
          const value = formData[field.name];
          if (!value || (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')) {
            newErrors[field.name] = `${field.label} is required`;
          }
        }
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setLoading(true);
      try {
        const updateData = {
          data: formData
        };

        // Add Checklist if any are required
        const requiredDocs = getRequiredDocumentVerifications();
        if (requiredDocs.length > 0) {
          updateData.documentVerifications = requiredDocs.map(doc => {
            const uploadedFile = documentUploads[doc._id];
            
            // If file was uploaded, use the uploaded file URL
            // Otherwise, use the custom document's file URL as default
            const fileUrl = uploadedFile 
              ? uploadedFile.fileUrl 
              : (doc.file ? doc.file.url : null);
            
            const fileName = uploadedFile 
              ? uploadedFile.fileName 
              : (doc.file ? doc.file.name : doc.name);
            
            return {
              documentId: doc._id,
              documentName: doc.name,
              fileUrl: fileUrl,
              fileName: fileName,
              files:  []
            };
          });
        }

        console.log('EditSubmissionModal handleSubmit - updateData:', updateData);
        console.log('Document uploads state:', documentUploads);

        await onSave(updateData);
        onClose();
      } catch (error) {
        console.error('Error updating submission:', error);
      } finally {
        setLoading(false);
      }
    };

    const renderField = (field) => {
      const value = formData[field.name] || '';
      const error = errors[field.name];

      switch (field.type) {
        case 'text':
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          );

        case 'number':
          return (
            <input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          );

        case 'date':
          return (
            <input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          );

        case 'dropdown':
          return (
            <select
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">Select {field.label.toLowerCase()}</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );

        case 'multitext':
          return (
            <textarea
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          );

        case 'upload':
          return (
            <div className="space-y-2">
              {/* Show existing files if any */}
              {value && Array.isArray(value) && value.length > 0 ? (
                <div className="space-y-2">
                  {value.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 text-xs font-medium">📄</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {typeof file === 'string' ? 'File uploaded' : (file.fileName || file.name || 'File')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {typeof file === 'string' ? 'File' : (file.mimeType || 'Unknown type')} • 
                            {typeof file === 'string' ? 'Unknown size' : (file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {typeof file === 'string' ? (
                          <a
                            href={file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View
                          </a>
                        ) : (
                          <a
                            href={file.fileUrl || file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Download
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const newFiles = value.filter((_, i) => i !== index);
                            handleInputChange(field.name, newFiles.length > 0 ? newFiles : []);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Replace/Add more files */}
                  <div className="text-center pt-2">
                    <input
                      type="file"
                      onChange={(e) => {
                        console.log('Replace/Add Files input change event:', e.target.files);
                        const files = Array.from(e.target.files);
                        console.log('Files array:', files);
                        
                        // Upload new files and add them to existing files
                        const uploadAndAddFiles = async () => {
                          const uploadedFiles = [];
                          for (const file of files) {
                            if (file instanceof File) {
                              const uploadedFile = await uploadFileToServer(file);
                              if (uploadedFile) {
                                uploadedFiles.push(uploadedFile);
                              }
                            }
                          }
                          
                          // Combine existing files with new uploaded files
                          const allFiles = [...value, ...uploadedFiles];
                          handleInputChange(field.name, allFiles);
                        };
                        
                        uploadAndAddFiles();
                      }}
                      multiple
                      className="hidden"
                      id={`file-upload-replace-${field.name}`}
                    />
                    <label
                      htmlFor={`file-upload-replace-${field.name}`}
                      className="cursor-pointer inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Add More Files
                    </label>
                  </div>
                </div>
              ) : (
                /* Show upload interface when no files exist */
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      Drag and drop files here or click to browse
                    </p>
                    <input
                      type="file"
                      onChange={(e) => {
                        console.log('File input change event:', e.target.files);
                        const files = Array.from(e.target.files);
                        console.log('Files array:', files);
                        
                        // Upload files and set them
                        const uploadFiles = async () => {
                          const uploadedFiles = [];
                          for (const file of files) {
                            if (file instanceof File) {
                              const uploadedFile = await uploadFileToServer(file);
                              if (uploadedFile) {
                                uploadedFiles.push(uploadedFile);
                              }
                            }
                          }
                          handleInputChange(field.name, uploadedFiles);
                        };
                        
                        uploadFiles();
                      }}
                      multiple
                      className="hidden"
                      id={`file-upload-${field.name}`}
                    />
                    <label
                      htmlFor={`file-upload-${field.name}`}
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Choose Files
                    </label>
                  </div>
                </div>
              )}
            </div>
          );

        case 'masterdata':
          const filteredMasterData = masterDataList.filter(item =>
            item.clientName?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
            item.clientEmail?.toLowerCase().includes(masterDataSearchTerm.toLowerCase())
          );

          return (
            <div className="relative masterdata-dropdown">
              <input
                type="text"
                value={masterDataSearchTerm}
                onChange={(e) => {
                  setMasterDataSearchTerm(e.target.value);
                  setShowMasterDataDropdown(true);
                }}
                onFocus={() => setShowMasterDataDropdown(true)}
                onKeyPress={(e) => handleHeaderFilterKeyPress('masterdata', e)}
                placeholder={value ? value.clientName : "Search master data..."}
                className={`
                  w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                  }
                `}
              />
              {showMasterDataDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredMasterData.map((item) => (
                    <div
                      key={item._id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        handleInputChange(field.name, item);
                        setMasterDataSearchTerm(item.clientName);
                        setShowMasterDataDropdown(false);
                      }}
                    >
                      <div className="font-medium">{item.clientName}</div>
                      <div className="text-sm text-gray-500">{item.clientEmail}</div>
                    </div>
                  ))}
                  {filteredMasterData.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">No master data found</div>
                  )}
                </div>
              )}
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange(field.name, null);
                    setMasterDataSearchTerm('');
                  }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          );

        case 'user':
          const filteredUsers = userList.filter(user =>
            `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
          );

          return (
            <div className="relative user-dropdown">
              <input
                type="text"
                value={userSearchTerm}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => setShowUserDropdown(true)}
                onKeyPress={(e) => handleHeaderFilterKeyPress('user', e)}
                placeholder={value ? `${value.firstName} ${value.lastName}` : "Search users..."}
                className={`
                  w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                  }
                `}
              />
              {showUserDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredUsers.map((user) => (
                    <div
                      key={user._id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        handleInputChange(field.name, user);
                        setUserSearchTerm(`${user.firstName} ${user.lastName}`);
                        setShowUserDropdown(false);
                      }}
                    >
                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">No users found</div>
                  )}
                </div>
              )}
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange(field.name, null);
                    setUserSearchTerm('');
                  }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          );

        default:
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          );
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit Submission</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Form Fields */}
              {customPage?.fields?.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {errors[field.name] && (
                    <p className="mt-1 text-sm text-red-600">{errors[field.name]}</p>
                  )}
                </div>
              ))}

              {/* Document Verifications */}
              {(() => {
                const requiredDocs = getRequiredDocumentVerifications();
                return requiredDocs.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Document Verifications</h3>
                    <div className="space-y-4">
                      {requiredDocs.map((docVer) => (
                      <div key={docVer._id} className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {docVer.name}
                        </label>
                        <input
                          type="file"
                          onChange={(e) => handleDocumentUpload(docVer._id, e.target.files[0])}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                        {documentUploads[docVer._id] && (
                          <p className="mt-1 text-sm text-gray-600">
                            Current file: {documentUploads[docVer._id].fileName || documentUploads[docVer._id].originalName}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  </div>
                );
              })()}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );

  }; // Close EditSubmissionModal component

  // Import Excel handlers
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setExcelFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          toast.error('Excel file is empty');
          return;
        }

        // First row is headers
        const headers = jsonData[0].map(h => String(h || '').trim()).filter(h => h);
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''));

        setExcelHeaders(headers);
        setExcelData(rows);

        // Initialize field mappings - auto-map by name if possible
        const initialMappings = {};
        if (customPage && customPage.fields) {
          customPage.fields.forEach(field => {
            const matchingHeader = headers.find(h => 
              h.toLowerCase() === field.name.toLowerCase() || 
              h.toLowerCase() === field.label.toLowerCase()
            );
            if (matchingHeader) {
              initialMappings[field.name] = matchingHeader;
            }
          });
        }
        setFieldMappings(initialMappings);

        // Initialize date formats for date fields
        const initialDateFormats = {};
        if (customPage && customPage.fields) {
          customPage.fields.forEach(field => {
            if (field.type === 'date') {
              initialDateFormats[field.name] = 'DD/MM/YYYY';
            }
          });
        }
        setDateFormats(initialDateFormats);

        toast.success(`Excel file loaded: ${headers.length} columns, ${rows.length} rows`);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!excelFile || excelData.length === 0) {
      toast.error('Please upload and review the Excel file first');
      return;
    }

    // Validate mappings for required fields
    if (customPage && customPage.fields) {
      const requiredFields = customPage.fields.filter(f => f.required);
      for (const field of requiredFields) {
        if (!fieldMappings[field.name]) {
          toast.error(`Please map the required field: ${field.label}`);
          return;
        }
      }
    }

    setImporting(true);
    setImportProgress({ total: excelData.length, imported: 0, skipped: 0, errors: [], rowResults: [] });

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('fieldMappings', JSON.stringify(fieldMappings));
      formData.append('dateFormats', JSON.stringify(dateFormats));
      formData.append('skipLogicColumns', JSON.stringify(skipLogicColumns));
      formData.append('statusMapping', JSON.stringify(statusMapping));
      formData.append('deadlineTypeMapping', JSON.stringify(deadlineTypeMapping));

      const response = await api.post(`/custom-pages/${pageId}/submissions/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          // This is for file upload progress, not import progress
        },
      });

      if (response.data.success) {
        const result = response.data.data;
        setImportProgress({
          total: result.total,
          imported: result.imported,
          skipped: result.skipped,
          errors: result.errors || [],
          rowResults: result.rowResults || []
        });

        if (result.imported > 0) {
          toast.success(`Successfully imported ${result.imported} records`);
          fetchSubmissions(); // Refresh the table
        }
        if (result.skipped > 0) {
          toast.warning(`${result.skipped} records were skipped (duplicates or errors)`);
        }
        if (result.errors && result.errors.length > 0) {
          console.error('Import errors:', result.errors);
        }
      } else {
        throw new Error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing Excel:', error);
      toast.error(error.response?.data?.message || 'Failed to import Excel file');
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setExcelFile(null);
    setExcelData([]);
    setExcelHeaders([]);
    setFieldMappings({});
    setDateFormats({});
    setSkipLogicColumns([]);
    setStatusMapping({ type: 'default', column: '', defaultStatus: 'submitted' });
    setDeadlineTypeMapping({ type: 'default', column: '', defaultType: 'personal' });
    setImportProgress({ total: 0, imported: 0, skipped: 0, errors: [], rowResults: [] });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{customPage.menuName} Submissions</h1>
          <p className="text-gray-600">{customPage.description}</p>
        </div>
        <div className="flex space-x-2">
          <ExportButtonsWithAll
            data={submissions}
            fileName={`${customPage.menuName}_Submissions`}
            columns={getExportFields()}
            pdfOptions={{ title: `${customPage.menuName} Submissions`, orientation: 'landscape' }}
            fetchAllData={fetchAllSubmissions}
            totalCount={pagination.total}
          />
          {canCreate && (
            <>
              <button
                onClick={() => setShowImportDialog(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Import Excel
              </button>
              <button
                onClick={() => navigate(`/custom-page/${pageId}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Submission
              </button>
            </>
          )}
        </div>
      </div>

      {/* Dashboard: Status (custom) + Deadline counts */}
      <div className="bg-white rounded-lg shadow p-4 overflow-hidden">
        <div className="dashboard-scroll flex items-center gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Status:</span>
            <div className="flex flex-wrap items-center gap-2">
              {submissionStats.statusCounts && Object.entries(submissionStats.statusCounts).length > 0 ? (
                Object.entries(submissionStats.statusCounts).map(([status, count]) => (
                  <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    {status}: <strong className="ml-1">{count}</strong>
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">—</span>
              )}
            </div>
          </div>
          <span className="text-gray-300 shrink-0">|</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Deadline:</span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-800">
                Overdue: <strong className="ml-1">{submissionStats.deadlineCounts?.overdue ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-800">
                Today: <strong className="ml-1">{submissionStats.deadlineCounts?.today ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-800">
                Next 7 days: <strong className="ml-1">{submissionStats.deadlineCounts?.next7 ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                Next 30 days: <strong className="ml-1">{submissionStats.deadlineCounts?.next30 ?? 0}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Controls Bar */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Table Controls */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          {/* Left side: Status Toggle Buttons */}
          <div className="flex items-center space-x-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
            {(() => {
              // Use custom statuses if they exist and have items, otherwise use default
              let statusList = (customPage?.customStatuses && Array.isArray(customPage.customStatuses) && customPage.customStatuses.length > 0)
                ? [...customPage.customStatuses]
                : ['draft', 'submitted', 'approved', 'rejected', 'completed'];
              
              // Ensure 'submitted' status is always included
              if (!statusList.includes('submitted')) {
                statusList = [...statusList, 'submitted'];
              }
              
              return statusList.map((status) => {
                const isSelected = selectedStatuses.includes(status);
                return (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                );
              });
            })()}
          </div>

          {/* Right side: Pagination controls */}
          <div className="flex items-center space-x-4">
            {/* Show Rows Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Show</label>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
                }}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <label className="text-sm text-gray-700">Rows</label>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-700">
              {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>

            {/* Filter Icon with Badge */}
            {(Object.keys(headerFilters).length > 0 || selectedStatuses.length > 0 || Object.keys(dateRangeFilters).length > 0) && (
              <div className="relative">
                <button
                  onClick={() => {
                    clearAllHeaderFilters();
                  }}
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                  title="Clear all filters"
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span className="absolute top-0 right-0 h-4 w-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {(Object.keys(headerFilters).length + selectedStatuses.length + Object.keys(dateRangeFilters).length)}
                  </span>
                </button>
              </div>
            )}

            {/* Save View Button */}
            <button
              onClick={() => setShowSaveViewDialog(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Save Current View"
            >
              <BookmarkIcon className="h-5 w-5" />
            </button>

            {/* Load View Button */}
            <button
              onClick={() => setShowLoadViewDialog(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md relative"
              title="Load Saved View"
            >
              <StarIcon className="h-5 w-5" />
              {savedViews.length > 0 && (
                <span className="absolute top-0 right-0 h-4 w-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {savedViews.length}
                </span>
              )}
            </button>

            {/* Column Visibility Icon */}
            <div className="relative column-visibility-dropdown">
              <button
                ref={columnVisibilityButtonRef}
                onClick={() => {
                  if (columnVisibilityButtonRef.current) {
                    const rect = columnVisibilityButtonRef.current.getBoundingClientRect();
                    setColumnVisibilityPosition({
                      top: rect.bottom + window.scrollY + 8,
                      right: window.innerWidth - rect.right + window.scrollX
                    });
                  }
                  setShowColumnVisibility(!showColumnVisibility);
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                title="Column Visibility"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
            </div>
            
            {/* Column Visibility Dropdown - Fixed positioning to avoid overflow clipping */}
            {showColumnVisibility && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowColumnVisibility(false)}
                />
                <div 
                  className="fixed w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto column-visibility-dropdown-content"
                  style={{
                    top: columnVisibilityPosition.top ? `${columnVisibilityPosition.top}px` : 'auto',
                    right: columnVisibilityPosition.right ? `${columnVisibilityPosition.right}px` : '16px',
                    bottom: columnVisibilityPosition.top ? 'auto' : '16px'
                  }}
                >
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-700 mb-2 px-2">Column Visibility</div>
                    {getAllTableFields().map((field) => (
                      <label 
                        key={field.key} 
                        className="flex items-center px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={columnVisibility[field.key] !== false}
                          onChange={(e) => setColumnVisibility(prev => ({ ...prev, [field.key]: e.target.checked }))}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Pagination Arrows */}
            {pagination.total > 0 && (
              <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  title="Previous"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages || pagination.pages === 0}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  title="Next"
            >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
          </div>
            )}
        </div>
      </div>

        {/* Filter summary */}
        {(Object.keys(headerFilters).length > 0 || selectedStatuses.length > 0 || Object.keys(dateRangeFilters).length > 0) && (
          <div className="px-4 py-2 bg-blue-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-blue-700 flex-wrap">
                <FunnelIcon className="h-4 w-4" />
                <span>Active filters:</span>
                {selectedStatuses.length > 0 && (
                  <span className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                    Status: {selectedStatuses.join(', ')}
                  </span>
                )}
                {Object.keys(headerFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const filterValue = headerFilters[key];
                  if (Array.isArray(filterValue) && filterValue.length > 0) {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {filterValue.join(', ')}
                      </span>
                    );
                  } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: "{filterValue}"
                      </span>
                    );
                  }
                  return null;
                })}
                {Object.keys(dateRangeFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const range = dateRangeFilters[key];
                  if (range && (range.from || range.to)) {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {range.from || '...'} to {range.to || '...'}
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              <button
                onClick={() => {
                  clearAllHeaderFilters();
                }}
                disabled={filterLoading}
                className={`text-sm underline ${
                  filterLoading 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-blue-600 hover:text-blue-800'
                }`}
              >
                {filterLoading ? 'Clearing filters...' : 'Clear all filters'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Dynamic columns based on showInTable fields */}
                {tableFields.map((field) => (
                  <th key={field.key} className="px-4 py-2">
                    <SortableHeader
                      field={field}
                      onSort={handleSort}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filterLoading ? (
                <tr>
                  <td colSpan={tableFields.length} className="px-4 py-6 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Applying filters...</span>
                    </div>
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => (
                  <tr 
                    key={submission._id} 
                    onClick={() => handleViewDetails(submission)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {/* Dynamic field columns */}
                    {tableFields.map((field) => (
                      <td key={field.key} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {field.render(submission)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {submissions.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Submission Details Modal */}
      {showDetails && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowDetails(false);
            setSelectedSubmission(null);
          }}
          onStatusChange={handleStatusChange}
          onRefreshDashboard={fetchSubmissionStats}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={handleEdit}
          onArchive={handleArchive}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this submission? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDelete(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Excel Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-6xl mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Import Excel</h2>
              <button
                onClick={() => {
                  setShowImportDialog(false);
                  resetImportDialog();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Step 1: Upload File */}
            {!excelFile && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Excel File
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Supported formats: .xlsx, .xls
                </p>
              </div>
            )}

            {/* Step 2: Preview and Mapping */}
            {excelFile && excelData.length > 0 && (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Excel Preview</h3>
                    <button
                      onClick={() => {
                        setExcelFile(null);
                        setExcelData([]);
                        setExcelHeaders([]);
                        setFieldMappings({});
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Change File
                    </button>
                  </div>
                  <div className="border border-gray-300 rounded-md overflow-auto max-h-64">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase sticky left-0 bg-gray-50 z-10">
                            Row
                          </th>
                          {excelHeaders.map((header, idx) => (
                            <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                              {header}
                            </th>
                          ))}
                          {importProgress.rowResults && importProgress.rowResults.length > 0 && (
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase sticky right-0 bg-gray-50 z-10">
                              Status
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {excelData.slice(0, Math.min(20, excelData.length)).map((row, rowIdx) => {
                          // Map row index to Excel row number (rowIdx + 2 because: 0-based index + 1 for header + 1 for Excel row number)
                          const excelRowNumber = rowIdx + 2;
                          const rowResult = importProgress.rowResults?.find(r => r.rowNumber === excelRowNumber);
                          const rowStatus = rowResult?.status || 'pending';
                          const rowMessage = rowResult?.message || '';
                          
                          return (
                            <tr 
                              key={rowIdx} 
                              className={
                                rowStatus === 'imported' 
                                  ? 'bg-green-50 hover:bg-green-100' 
                                  : rowStatus === 'skipped' 
                                  ? 'bg-yellow-50 hover:bg-yellow-100' 
                                  : ''
                              }
                            >
                              <td className="px-3 py-2 text-sm text-gray-900 font-medium sticky left-0 bg-inherit z-10">
                                {excelRowNumber}
                              </td>
                            {excelHeaders.map((header, colIdx) => (
                              <td key={colIdx} className="px-3 py-2 text-sm text-gray-900">
                                {row[colIdx] !== undefined && row[colIdx] !== null ? String(row[colIdx]) : ''}
                              </td>
                            ))}
                              {importProgress.rowResults && importProgress.rowResults.length > 0 && (
                                <td className="px-3 py-2 text-sm sticky right-0 bg-inherit z-10">
                                  {rowStatus === 'imported' ? (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                      ✓ Imported
                                    </span>
                                  ) : rowStatus === 'skipped' ? (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800" title={rowMessage}>
                                      ⚠ Skipped
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                      Pending
                                    </span>
                                  )}
                                </td>
                              )}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {excelData.length > 20 && (
                      <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        ... and {excelData.length - 20} more rows
                      </div>
                    )}
                  </div>
                </div>

                {/* Field Mapping */}
                {customPage && customPage.fields && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Mapping</h3>
                    <div className="space-y-4">
                      {customPage.fields.map((field) => (
                        <div key={field.name} className="flex items-center space-x-4">
                          <div className="w-1/3">
                            <label className="block text-sm font-medium text-gray-700">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                              <span className="text-xs text-gray-500 ml-2">({field.type})</span>
                            </label>
                          </div>
                          <div className="w-2/3">
                            <select
                              value={fieldMappings[field.name] || ''}
                              onChange={(e) => setFieldMappings(prev => ({
                                ...prev,
                                [field.name]: e.target.value
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">-- Select Excel Column --</option>
                              {excelHeaders.map((header, idx) => (
                                <option key={idx} value={header}>{header}</option>
                              ))}
                            </select>
                            {field.type === 'date' && fieldMappings[field.name] && (
                              <div className="mt-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Date Format
                                </label>
                                <select
                                  value={dateFormats[field.name] || 'DD/MM/YYYY'}
                                  onChange={(e) => setDateFormats(prev => ({
                                    ...prev,
                                    [field.name]: e.target.value
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                  <option value="DD-MON-YY">DD-MON-YY / D-MON-YY (e.g., 6-Oct-22, 27-Sep-12, or NA)</option>
                                  <option value="DD-MON-YYYY">DD-MON-YYYY</option>
                                  <option value="MON-DD-YYYY">MON-DD-YYYY</option>
                                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                  <option value="DD/MM/YY">DD/MM/YY</option>
                                  <option value="MM/DD/YY">MM/DD/YY</option>
                                  <option value="auto">Auto-detect (handles all formats including NA)</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Mapping */}
                {excelHeaders.length > 0 && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Status Mapping</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose how to set the status for imported records: map to an Excel column or use a default status.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="statusMappingType"
                            value="default"
                            checked={statusMapping.type === 'default'}
                            onChange={(e) => setStatusMapping(prev => ({ ...prev, type: 'default' }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Use Default Status</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="statusMappingType"
                            value="column"
                            checked={statusMapping.type === 'column'}
                            onChange={(e) => setStatusMapping(prev => ({ ...prev, type: 'column' }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Map from Excel Column</span>
                        </label>
                      </div>

                      {statusMapping.type === 'default' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Status
                          </label>
                          <select
                            value={statusMapping.defaultStatus}
                            onChange={(e) => setStatusMapping(prev => ({ ...prev, defaultStatus: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {(() => {
                              let statusList = (customPage?.customStatuses && Array.isArray(customPage.customStatuses) && customPage.customStatuses.length > 0)
                                ? [...customPage.customStatuses]
                                : ['draft', 'submitted', 'approved', 'rejected', 'completed'];
                              
                              // Ensure 'submitted' status is always included
                              if (!statusList.includes('submitted')) {
                                statusList = [...statusList, 'submitted'];
                              }
                              
                              return statusList.map((status) => (
                                <option key={status} value={status}>
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>
                      )}

                      {statusMapping.type === 'column' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Excel Column for Status
                          </label>
                          <select
                            value={statusMapping.column}
                            onChange={(e) => setStatusMapping(prev => ({ ...prev, column: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Select Excel Column --</option>
                            {excelHeaders.map((header, idx) => (
                              <option key={idx} value={header}>{header}</option>
                            ))}
                          </select>
                          <p className="mt-2 text-xs text-gray-500">
                            Status values from the Excel column will be validated against the custom status list. Invalid values will use the default status.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Deadline Type Mapping */}
                {excelHeaders.length > 0 && (
                  <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-md">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Deadline Type Mapping</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose how to set the deadline type (Personal/Group) for imported records: map to an Excel column or use a default value.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="deadlineTypeMappingType"
                            value="default"
                            checked={deadlineTypeMapping.type === 'default'}
                            onChange={(e) => setDeadlineTypeMapping(prev => ({ ...prev, type: 'default' }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Use Default Type</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="deadlineTypeMappingType"
                            value="column"
                            checked={deadlineTypeMapping.type === 'column'}
                            onChange={(e) => setDeadlineTypeMapping(prev => ({ ...prev, type: 'column' }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Map from Excel Column</span>
                        </label>
                      </div>

                      {deadlineTypeMapping.type === 'default' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Deadline Type
                          </label>
                          <select
                            value={deadlineTypeMapping.defaultType}
                            onChange={(e) => setDeadlineTypeMapping(prev => ({ ...prev, defaultType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="personal">Personal</option>
                            <option value="group">Group</option>
                          </select>
                        </div>
                      )}

                      {deadlineTypeMapping.type === 'column' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Excel Column for Deadline Type
                          </label>
                          <select
                            value={deadlineTypeMapping.column}
                            onChange={(e) => setDeadlineTypeMapping(prev => ({ ...prev, column: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Select Excel Column --</option>
                            {excelHeaders.map((header, idx) => (
                              <option key={idx} value={header}>{header}</option>
                            ))}
                          </select>
                          <p className="mt-2 text-xs text-gray-500">
                            Values should be "personal" or "group" (case-insensitive). Invalid values will use the default type.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Skip Logic Configuration */}
                {excelHeaders.length > 0 && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Skip Logic Configuration</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Select Excel columns to check for duplicates. If a record with the same values in these columns already exists, it will be skipped during import.
                    </p>
                    <div className="space-y-2">
                      {excelHeaders.map((header, idx) => (
                        <label key={idx} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={skipLogicColumns.includes(header)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSkipLogicColumns(prev => [...prev, header]);
                              } else {
                                setSkipLogicColumns(prev => prev.filter(col => col !== header));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{header}</span>
                        </label>
                      ))}
                    </div>
                    {skipLogicColumns.length > 0 && (
                      <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-800">
                        <strong>Selected columns for duplicate checking:</strong> {skipLogicColumns.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Import Progress */}
                {importing && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-900">Importing...</span>
                      <span className="text-sm text-blue-700">
                        {importProgress.imported + importProgress.skipped} / {importProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${((importProgress.imported + importProgress.skipped) / importProgress.total) * 100}%`
                        }}
                      />
                    </div>
                    <div className="mt-2 text-sm text-blue-700">
                      Imported: {importProgress.imported} | Skipped: {importProgress.skipped}
                    </div>
                  </div>
                )}

                {/* Import Results */}
                {!importing && importProgress.total > 0 && (
                  <div className="mb-6">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-md mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Import Summary</h4>
                    <div className="text-sm text-gray-700 space-y-1">
                      <div>Total Records: {importProgress.total}</div>
                        <div className="text-green-600 font-medium">Imported: {importProgress.imported}</div>
                        <div className="text-yellow-600 font-medium">Skipped: {importProgress.skipped}</div>
                      </div>
                    </div>

                    {/* Detailed Row Results */}
                    {importProgress.rowResults && importProgress.rowResults.length > 0 && (
                      <div className="border border-gray-200 rounded-md overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-900">Detailed Results</h4>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Row</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Message</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Details</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {importProgress.rowResults.map((rowResult, idx) => (
                                <tr key={idx} className={rowResult.status === 'imported' ? 'bg-green-50' : 'bg-yellow-50'}>
                                  <td className="px-4 py-2 whitespace-nowrap text-gray-900 font-medium">
                                    {rowResult.rowNumber}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      rowResult.status === 'imported' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {rowResult.status === 'imported' ? 'Imported' : 'Skipped'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-700">
                                    {rowResult.message || '-'}
                                  </td>
                                  <td className="px-4 py-2 text-gray-600 text-xs">
                                    {rowResult.data && Object.keys(rowResult.data).length > 0 ? (
                                      <div className="space-y-1">
                                        {Object.entries(rowResult.data).map(([key, value]) => {
                                          if (typeof value === 'object' && value.masterDataId) {
                                            return (
                                              <div key={key} className="text-xs">
                                                <span className="font-medium">{value.label}:</span> {value.value} 
                                                <span className="text-blue-600 ml-1">(Master Data ID: {value.masterDataId})</span>
                                              </div>
                                            );
                                          }
                                          return null;
                                        })}
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Errors Summary */}
                      {importProgress.errors.length > 0 && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <div className="text-red-600 font-semibold mb-2">Errors Summary</div>
                        <ul className="list-disc list-inside text-xs text-red-600 max-h-32 overflow-y-auto">
                          {importProgress.errors.slice(0, 10).map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          {importProgress.errors.length > 10 && (
                            <li>... and {importProgress.errors.length - 10} more errors</li>
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowImportDialog(false);
                      resetImportDialog();
                    }}
                    disabled={importing}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {importProgress.total > 0 ? 'Close' : 'Cancel'}
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing || !excelFile || excelData.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Submission Modal */}
      {(() => {
        console.log('Modal rendering check:', {
          showEdit,
          selectedSubmission: !!selectedSubmission,
          customPage: !!customPage,
          allConditions: showEdit && selectedSubmission && customPage
        });
        return showEdit && selectedSubmission && customPage;
      })() && (
        <EditSubmissionModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowEdit(false);
            setSelectedSubmission(null);
            setEditFormData({});
          }}
          onSave={handleEditSave}
        />
      )}

      {/* Filter Dialog */}
      {filterDialog.open && filterDialog.field && (
        <FilterDialog
          field={filterDialog.field}
          onClose={() => setFilterDialog({ open: false, field: null })}
        />
      )}

      {/* Save View Dialog */}
      {showSaveViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Save Current View</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">View Name</label>
              <input
                type="text"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="Enter view name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Set as default view</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">Default view will be automatically loaded when you open this page</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <p className="text-sm text-gray-700 font-medium mb-2">This view will save:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Column visibility settings</li>
                <li>• Sort field and direction</li>
              </ul>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowSaveViewDialog(false); setNewViewName(''); setSaveAsDefault(false); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={savingView}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveView}
                disabled={savingView || !newViewName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingView ? 'Saving...' : 'Save View'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load View Dialog */}
      {showLoadViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Load Saved View</h2>
            {loadingViews ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : savedViews.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No saved views yet</p>
                <p className="text-sm text-gray-500 mt-2">Save your current view settings to quickly access them later</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedViews.map((view) => (
                  <div
                    key={view._id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${currentViewId === view._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{view.name}</h3>
                          {view.isDefault && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              <StarIconSolid className="h-3 w-3 mr-1" /> Default
                            </span>
                          )}
                          {currentViewId === view._id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Active</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Created: {new Date(view.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button onClick={() => handleLoadView(view)} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Load</button>
                        {!view.isDefault && (
                          <button onClick={() => handleSetDefaultView(view._id)} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200" title="Set as default">
                            <StarIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => handleDeleteView(view._id)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={() => setShowLoadViewDialog(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomPageSubmissionsTable;